import { FormattedMessage } from 'react-intl';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { useCallback, useEffect, useRef } from 'react';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import CoinsSvg from './assets/coins.svg';
import Image from '@atlaskit/image/image';

const bannerStyles = cssMap({
	banner: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.100'),
		transitionProperty: 'color, background-color',
		transitionDuration: '200ms',
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
	},
	button: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		textAlign: 'left',
		backgroundColor: 'transparent',
		width: '100%',
	},
});

export function SideNavBanner({
	onClick,
	onDismiss,
	productName,
	edition,
	bannerName,
	analyticsAttributes,
}: {
	onClick: () => void;
	onDismiss: () => void;
	productName: string;
	edition: string;
	bannerName: string;
	analyticsAttributes?: Record<string, unknown>;
}): JSX.Element {
	const hasFiredViewedRef = useRef(false);
	const { messageDismissed } = useMessageEvent();
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();

	const handleOnClick = useCallback(() => {
		onCtaActioned({ interactionIdentifier: 'bannerOpen' });
		onClick();
	}, [onCtaActioned, onClick]);

	const handleDismiss = useCallback(() => {
		void messageDismissed();
		onClosed({ interactionIdentifier: 'dismiss' });
		onDismiss();
	}, [messageDismissed, onClosed, onDismiss]);

	useEffect(() => {
		if (hasFiredViewedRef.current) return;
		hasFiredViewedRef.current = true;
		void messageInstanceViewed({
			name: bannerName,
			...analyticsAttributes,
		});
	}, [messageInstanceViewed, analyticsAttributes, bannerName]);

	return (
		<Flex xcss={bannerStyles.banner} gap="space.200" justifyContent="space-between">
			<Pressable
				xcss={bannerStyles.button}
				testId="recent-downgrade-discount-banner-cta"
				onClick={handleOnClick}
			>
				<Image src={CoinsSvg} alt="" height={32} width={32} />
				<Stack space="space.050">
					<Text size="medium" weight="medium">
						<FormattedMessage
							id="growth-transmuters.recent-downgrade-discount.banner.title"
							defaultMessage="Get 20% off 3 months of {productName} {edition}"
							description="Title of the sidenav banner for recent downgrade discount"
							values={{
								productName,
								edition,
							}}
						/>
					</Text>
					<Text size="medium" weight="medium" color="color.link">
						<FormattedMessage
							id="growth-transmuters.recent-downgrade-discount.banner.cta"
							defaultMessage="View offer"
							description="CTA link text on the sidenav banner for the recent downgrade discount"
						/>
					</Text>
				</Stack>
			</Pressable>
			<IconButton
				icon={CrossIcon}
				appearance="subtle"
				label="Close"
				isTooltipDisabled={false}
				onClick={handleDismiss}
				spacing="compact"
			/>
		</Flex>
	);
}
